import { Button, Card, Input, Space, Table, Tag, Tooltip, Typography } from "antd";
import React, { useMemo, useState } from "react";

import type { ColumnsType } from "antd/es/table";
import {
  EyeOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  ContainerOutlined,
  CalendarOutlined,
  FileOutlined,
  DollarOutlined,
  UserOutlined,
  CarOutlined,
  ShopOutlined,
  GlobalOutlined,
  BranchesOutlined,
  CheckCircleTwoTone,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
const { Title, Text } = Typography;

interface ResultadoProps {
  _id: string;
  forward_ref_number: string;
  armador: string;
  cliente: string;
  data_embarque: string;
  embarcador_email: string;
  porto_embarque: string;
  porto_descarga: string;
  selectMercadoria: string;
  tipo_container: string;
  qtdContainers: string;
  email: string;
  status: string;
  valor: string;
  oceanFreigth: string;
  created_at: string;
  bookingFile: string;
  blFile: string;
}

interface TabelaResultadosProps {
  response?: ResultadoProps[];
}

const TabelaResultados: React.FC<TabelaResultadosProps> = ({
  response = [],
}) => {
  const email = localStorage.getItem("user_email");
  const isAdmin =
    email === "ffleck@gmail.com" || email === "alvaro@karavel.com";
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;

  const safeDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime())
        ? "N/A"
        : date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
    } catch {
      return "N/A";
    }
  };

  const getStatusColor = (status: string | undefined): string => {
    if (!status) return "gray";
    switch (status.toLowerCase()) {
      case "pending":
        return "orange";
      case "canceled":
        return "red";
      case "confirmed":
        return "orange";
      case "deleted":
        return "red";
      case "loaded":
        return "blue";
      case "saved":
        return "cyan";
      default:
        return "gray";
    }
  };

  const getStatusText = (status: string | undefined): string => {
    if (!status) return "N/A";
    switch (status.toLowerCase()) {
      case "pending":
        return "PENDENTE";
      case "canceled":
        return "CANCELADO";
      case "confirmed":
        // return "CONFIRMADO";
        return "PENDENTE";
      case "deleted":
        return "EXCLUÍDO";
      case "loaded":
        return "CARREGADO";
      case "saved":
        return "SALVO";
      default:
        return status.toUpperCase();
    }
  };

  const filteredData = useMemo(() => {
    return response.filter((resultado) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (resultado.armador?.toLowerCase() || "").includes(searchLower) ||
        (isAdmin &&
          (resultado.cliente?.toLowerCase() || "").includes(searchLower)) ||
        (resultado.porto_embarque?.toLowerCase() || "").includes(searchLower) ||
        (resultado.porto_descarga?.toLowerCase() || "").includes(searchLower) ||
        (resultado.status?.toLowerCase() || "").includes(searchLower) ||
        (resultado.forward_ref_number?.toLowerCase() || "").includes(
          searchLower
        ) ||
        (resultado.selectMercadoria?.toLowerCase() || "").includes(
          searchLower
        ) ||
        (resultado.tipo_container?.toLowerCase() || "").includes(searchLower)
      );
    });
  }, [searchTerm, response, isAdmin]);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredData]);

  const columns: ColumnsType<ResultadoProps> = [
    {
      title: (
        <Tooltip title="Número do Pedido">
          <Space>
            <ContainerOutlined />
            #Pedido
          </Space>
        </Tooltip>
      ),
      dataIndex: "_id",
      key: "_id",
      width: 100,
      render: (id) => <Text copyable>{id?.substring(0, 6) || "N/A"}...</Text>,
      sorter: (a, b) => a._id.localeCompare(b._id),
    },

    {
      title: (
        <Tooltip title="Booking Number">
          <Space>
            <ContainerOutlined />
            Booking Number
          </Space>
        </Tooltip>
      ),
      dataIndex: "bookingNumber",
      key: "bookingNumber",
      width: 100,
      align: "center",
      render: (id) =>
        id?.substring(0, 6) ? (
          <Text copyable>{id?.substring(0, 6)}</Text>
        ) : (
          <Tag color="default">N/A</Tag>
        ),
      sorter: (a, b) => a._id.localeCompare(b._id),
    },
    {
      title: (
        <Tooltip title="Referência do Forwarder">
          <Space>
            <BranchesOutlined />
            Ref. Fwd
          </Space>
        </Tooltip>
      ),
      dataIndex: "forward_ref_number",
      key: "forward_ref_number",
      width: 140,
      render: (ref) => ref || <Tag color="default">N/A</Tag>,
      sorter: (a, b) =>
        (a.forward_ref_number || "").localeCompare(b.forward_ref_number || ""),
    },
    ...(isAdmin
      ? [
          {
            title: (
              <Tooltip title="Cliente do Booking">
                <Space>
                  <UserOutlined />
                  Cliente
                </Space>
              </Tooltip>
            ),
            dataIndex: "email",
            key: "email",
            width: 180,
            render: (mail) => mail || <Tag color="default">N/A</Tag>,
            sorter: (a, b) => a.email.localeCompare(b.email),
          },
        ]
      : []),
    {
      title: (
        <Tooltip title="Armador">
          <Space>
            <ShopOutlined />
            Armador
          </Space>
        </Tooltip>
      ),
      dataIndex: "armador",
      key: "armador",
      width: 120,
      render: (armador) => armador.toUpperCase() || <Tag color="default">N/A</Tag>,
      sorter: (a, b) => a.armador.localeCompare(b.armador),
    },
    {
      title: (
        <Tooltip title="Quantidade de Containers">
          <Space>
            <CarOutlined />
            Qtd Cont.
          </Space>
        </Tooltip>
      ),
      dataIndex: "qtdContainers",
      key: "qtdContainers",
      width: 110,
      align: "center",
      render: (qtd) => <b>{qtd || "0"}</b>,
      sorter: (a, b) => parseInt(a.qtdContainers) - parseInt(b.qtdContainers),
    },
    {
      title: (
        <Tooltip title="Estimated Time of Sailing">
          <Space>
            <CalendarOutlined />
            ETS
          </Space>
        </Tooltip>
      ),
      dataIndex: "data_embarque",
      key: "data_embarque",
      width: 120,
      render: safeDate,
      sorter: (a, b) =>
        new Date(a.data_embarque).getTime() -
        new Date(b.data_embarque).getTime(),
    },
    {
      title: (
        <Tooltip title="Porto de Embarque">
          <Space>
            <GlobalOutlined />
            POL
          </Space>
        </Tooltip>
      ),
      dataIndex: "porto_embarque",
      key: "porto_embarque",
      width: 120,
      render: (porto) => porto || <Tag color="default">N/A</Tag>,
      sorter: (a, b) => a.porto_embarque.localeCompare(b.porto_embarque),
    },
    {
      title: (
        <Tooltip title="Porto de Descarga">
          <Space>
            <GlobalOutlined />
            POD
          </Space>
        </Tooltip>
      ),
      dataIndex: "porto_descarga",
      key: "porto_descarga",
      width: 120,
      render: (porto) => porto || <Tag color="default">N/A</Tag>,
      sorter: (a, b) => a.porto_descarga.localeCompare(b.porto_descarga),
    },
    {
      title: (
        <Tooltip title="Data de Criação">
          <Space>
            <CalendarOutlined />
            Criação
          </Space>
        </Tooltip>
      ),
      dataIndex: "created_at",
      key: "created_at",
      width: 130,
      render: safeDate,
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: (
        <Tooltip title="Moeda">
          <Space>
            <DollarOutlined />
            Moeda
          </Space>
        </Tooltip>
      ),
      dataIndex: "oceanFreigth",
      key: "oceanFreigth",
      width: 90,
      render: (moeda) => moeda || <Tag color="default">N/A</Tag>,
    },
    {
      title: (
        <Tooltip title="Valor Total">
          <Space>
            <DollarOutlined />
            Valor
          </Space>
        </Tooltip>
      ),
      dataIndex: "valor",
      key: "valor",
      width: 100,
      render: (value) =>
        value ? (
          <b style={{ color: "#009688" }}>
            {parseFloat(value).toLocaleString("pt-BR", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
            })}
          </b>
        ) : (
          <Tag color="default">N/A</Tag>
        ),
      sorter: (a, b) => parseFloat(a.valor) - parseFloat(b.valor),
    },
    {
      title: (
        <Tooltip title="Status do Booking">
          <Space>
            <InfoCircleOutlined />
            Status
          </Space>
        </Tooltip>
      ),
      dataIndex: "status",
      key: "status",
      width: 120,
      fixed: "right",
      align: "center",
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ fontWeight: "bold" }}>
          {getStatusText(status)}
        </Tag>
      ),
      filters: [
        { text: "Pendente", value: "pending" },
        { text: "Cancelado", value: "canceled" },
        { text: "Confirmado", value: "confirmed" },
        { text: "Excluído", value: "deleted" },
        { text: "Carregado", value: "loaded" },
        { text: "Booking Salvo", value: "saved" },
      ],
      onFilter: (value, record) =>
        record.status.toLowerCase() === value.toString().toLowerCase(),
    },
    {
      title: (
        <Tooltip title="Arquivo Booking">
          <Space>
            <FileOutlined />
            Booking
          </Space>
        </Tooltip>
      ),
      dataIndex: "bookingFile",
      key: "bookingFile",
      width: 110,
      align: "center",
      render: (file) =>
        file ? (
          <Tooltip title="Download Booking File">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => window.open(file, "_blank")}
              style={{ color: "#2e82f7" }}
            />
          </Tooltip>
        ) : (
          <Tag color="default">N/A</Tag>
        ),
    },
    {
      title: (
        <Tooltip title="Arquivo BL">
          <Space>
            <FileOutlined />
            BL
          </Space>
        </Tooltip>
      ),
      dataIndex: "blFile",
      key: "blFile",
      width: 110,
      align: "center",
      render: (file) =>
        file ? (
          <Tooltip title="Download BL File">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => window.open(file, "_blank")}
              style={{ color: "#52c41a" }}
            />
          </Tooltip>
        ) : (
          <Tag color="default">N/A</Tag>
        ),
    },
    {
      title: (
        <Tooltip title="Visualizar detalhes do booking">
          <EyeOutlined />
        </Tooltip>
      ),
      key: "actions",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="Visualizar detalhes">
          <Link to={`/showBooking/${record._id}`}>
            <Button
              type="link"
              icon={<CheckCircleTwoTone twoToneColor="#2e82f7" />}
            />
          </Link>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card
      bordered={false}
      style={{
        width: "100%",
        margin: "18px 0",
        borderRadius: 16,
        boxShadow: "0 4px 32px #2e82f71a",
        background: "#fafdff",
      }}
      bodyStyle={{ padding: 20 }}
      headStyle={{ backgroundColor: "#f0f2f5", borderRadius: "16px 16px 0 0" }}
      title={
        <Space direction="vertical" size={0}>
          <Title level={3} style={{ marginBottom: 0, color: "#1890ff" }}>
            <ContainerOutlined style={{ marginRight: 10 }} />
            Lista de Bookings
          </Title>
          <Text type="secondary" style={{ fontSize: 15 }}>
            Visualize, filtre e pesquise todos os seus bookings registrados.
          </Text>
        </Space>
      }
      extra={
        <Input.Search
          placeholder="Buscar Booking, Cliente, Porto, Status..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: 280 }}
          allowClear
          enterButton="Buscar"
        />
      }
    >
      <Table
        columns={columns}
        dataSource={currentItems}
        rowKey="_id"
        pagination={{
          current: currentPage,
          pageSize: itemsPerPage,
          total: filteredData.length,
          onChange: (page) => setCurrentPage(page),
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} itens`,
        }}
        scroll={{ x: 1900 }}
        bordered
        size="middle"
        locale={{
          emptyText: "Nenhum registro encontrado",
          filterReset: "Limpar",
          filterConfirm: "Filtrar",
        }}
      />
    </Card>
  );
};

export default TabelaResultados;
